import { Link, RichTextEditor, type RichTextEditorLabels } from "@mantine/tiptap";
import Underline from "@tiptap/extension-underline";
import { useEditor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import { StarterKit } from "@tiptap/starter-kit";
import type { PropsWithChildren } from "react";
import styles from "./FormEditor.module.css";

type FormEditorProps = PropsWithChildren<{
  defaultValue?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  borderTop?: string;
  disabled?: boolean;
}>;

export function FormEditor({ defaultValue, onChange, children, borderTop, disabled = false }: FormEditorProps) {
  const editor = useEditor({
    extensions: [StarterKit, Underline, Link],
    content: defaultValue,
    onUpdate({ editor }) {
      const content = editor.getHTML();
      onChange?.(content as never);
    },
    editable: !disabled,
  });

  return (
    <RichTextEditor
      editor={editor}
      labels={labels}
      styles={{ root: { borderTop: borderTop } }}
      classNames={{ content: disabled ? styles.disabled : undefined }}
      data-disabled={disabled}
    >
      {!disabled && (
        <RichTextEditor.Toolbar sticky stickyOffset={60}>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Bold />
            <RichTextEditor.Italic />
            <RichTextEditor.Underline />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.H1 />
            <RichTextEditor.H2 />
            <RichTextEditor.H3 />
            <RichTextEditor.H4 />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.BulletList />
            <RichTextEditor.OrderedList />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Link />
          </RichTextEditor.ControlsGroup>

          {children}
        </RichTextEditor.Toolbar>
      )}
      {!disabled && (
        <BubbleMenu>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Bold />
            <RichTextEditor.Italic />
            <RichTextEditor.Underline />
            <RichTextEditor.Link />
          </RichTextEditor.ControlsGroup>
        </BubbleMenu>
      )}
      <RichTextEditor.Content />
    </RichTextEditor>
  );
}

const labels: Partial<RichTextEditorLabels> = {
  underlineControlLabel: "Unterschtreichen",
  boldControlLabel: "Fett",
  italicControlLabel: "Kursiv",
  bulletListControlLabel: "Liste",
  orderedListControlLabel: "Nummerierte Liste",
};
